/**
 * Admin-facing help for the Atlas Guided Workflows editor.
 *
 * Content is hand-distilled from the design docs — keep them in step:
 *   docs/atlas-workflows-design.md
 *   docs/atlas-content-recipe-design.md   (§6a drafting guide, §9a upload extract)
 *   docs/atlas-doc-upload-step-design.md
 *
 * Entries render in HelpTip.vue (info-icon menu cards): plain text only —
 * { title, body?, bullets? }. The long-form guides at the bottom render
 * in HelpGuideDialog.vue.
 */

export const WORKFLOW_HELP = {
  // ── Workflow level ────────────────────────────────────────────────
  'wf.status': {
    title: 'Workflow lifecycle',
    body: 'A workflow moves through three states.',
    bullets: [
      'Draft: editable, never offered to users. Publish when it validates cleanly.',
      'Published: users can start it (in the portal chat, and via MCP hosts when that switch is on).',
      'Inactive (Deactivate): hidden from use but the workflow and its run history stay. Publish brings it back.',
      'Delete only works for workflows no run has ever used; otherwise Deactivate is the retirement path.',
    ],
  },
  'wf.description': {
    title: 'Description',
    body: 'Atlas reads this when deciding whether to offer the workflow in conversation — write it as “when to use this”, not as documentation.',
    bullets: [
      'Example: “Guides a manager through creating a new job profile from scratch or from an uploaded document.”',
    ],
  },
  'wf.feedback': {
    title: 'Feedback score',
    body: 'Shows a small score strip next to the “Exit workflow” link on every card of a live run.',
    bullets: [
      'Values = how many points the scale has; the end labels anchor its meaning.',
      '“Ask why at or below” adds a follow-up question when the user scores low.',
      'Scores are stored against the run and appear in the Stats tab.',
    ],
  },
  'wf.copy_from_client': {
    title: 'Copy from another client',
    body: 'Copies a workflow across clients by re-identifying everything client-specific against this client’s own templates.',
    bullets: [
      'Template attributes match by name (and by levelling-framework role, e.g. their “Job Level” to our “Grade”); hierarchy types and chains match by name.',
      'Anything that doesn’t match degrades to a name reference and is listed in the match report — the draft saves, but publish is blocked until you re-pick those.',
      'The copy opens as a new draft with “-copy” added to the key.',
    ],
  },
  'wf.save_as': {
    title: 'Save As',
    body: 'Saves the open definition as an independent copy — useful for testing a variant alongside the original.',
    bullets: [
      'The copy starts as a draft; publish it to be able to start it.',
      'It gets its own internal identity, so the original can never be overwritten.',
    ],
  },

  // ── Step common ───────────────────────────────────────────────────
  'step.kind': {
    title: 'Step types',
    bullets: [
      'Choice: buttons, each wired to a next step.',
      'Job search: search criteria, inline results or hand-off to a list step.',
      'Document list: a fixed query or the previous search’s results; picking one carries it forward.',
      'Document view: shows the carried document read-only.',
      'Document create: a form, optionally drafting content and creating the document on submit.',
      'Document upload: file drop → extraction → gap-fill → review → create.',
      'End point: closes the run with guidance for Atlas.',
    ],
  },
  'step.start': {
    title: 'Start and entry points',
    body: 'The start step is where the workflow opens by default. Entry points are additional doors: Atlas can start the workflow directly at one when the user’s request matches its intent hint (“I want to upload a profile” → the upload step).',
  },
  'step.requires': {
    title: 'Permission to enter',
    body: 'The step is gated on the user holding this permission (view / edit / create), checked against the step’s own template context — a create form checks create rights on its template.',
    bullets: [
      'Options leading to a step the user can’t enter show disabled with the reason.',
      'Users who can’t use any path into the workflow don’t see it offered at all.',
    ],
  },
  'step.prompt': {
    title: 'Card prompt',
    body: 'The text shown on the card itself. Atlas adds its own one-line introduction around the card, so the prompt should carry the instruction, not the pleasantries.',
  },
  'step.guidance': {
    title: 'Guidance for Atlas',
    body: 'Not shown to the user — this steers what Atlas says and does while the step is showing (or, on an end point, what it should do when the run finishes there).',
    bullets: [
      'Example: “If the user asks what a job family is, explain briefly and point them back to the picker.”',
      'Don’t restate what the card already shows, and don’t tell Atlas to run tools the step runs itself.',
    ],
  },

  // ── job_search / doc_list / doc_view ──────────────────────────────
  'search.document_types': {
    title: 'Document types',
    body: 'Restricts the search to these template types. Blank searches the client’s defaults. This also scopes the permission check and, on a next-step hand-off, what the list step shows.',
  },
  'search.results_mode': {
    title: 'Where results appear',
    bullets: [
      'In this card: results list under the search box; picking one follows the Selected link.',
      'Pass to next step: this card only collects criteria — Search hands them to a Document list step set to “from the previous search”. If nothing matches, this search card is re-shown automatically.',
    ],
  },
  'search.fields': {
    title: 'Extra search fields',
    body: 'Structured criteria alongside (or instead of) the keyword box — the same fields the All Jobs screen searches on.',
    bullets: [
      'Values can be fixed options, a template attribute’s lookup values, or a hierarchy (level 2+ renders a tree picker).',
      'Req makes the card refuse to search until the field is filled.',
      'Hiding the keyword box makes these fields the whole search.',
    ],
  },

  'doclist.source': {
    title: 'What the list shows',
    bullets: [
      'Fixed query: the keyword / doc types / statuses configured here, resolved fresh each time.',
      'From a previous search: re-runs the criteria captured by the named search step (or the step just before). Pair with a job search set to “pass to next step”.',
      'Only documents the user is allowed to view are listed, capped at the row limit.',
    ],
  },
  'docview.source_step': {
    title: 'Which document is shown',
    body: 'The document carried forward from an earlier step — a list selection or an upload. Blank means “the step just before”. Leaving the confirmed link blank makes this a display-only end of the workflow.',
  },

  // ── doc_upload ────────────────────────────────────────────────────
  'upload.template': {
    title: 'Target template',
    body: 'The template the uploaded document becomes. It drives everything: which sections can be extracted, which attributes are captured automatically, the mandatory-field gap-fill, the duplicate check and the final create.',
  },
  'upload.extraction_mode': {
    title: 'Extraction',
    body: 'How the uploaded document’s text becomes a draft.',
    bullets: [
      'Extract with Atlas (simple): one structured call pulls the sections listed below straight from the document. The template’s capturable attributes and hierarchy placements are captured automatically — nothing to author for those. Extraction preserves the document’s wording; it never invents or summarises.',
      'Extract with a prompt chain: the legacy path — the chain must be linked to the target template, and the chosen input receives the document text.',
      'Either way the same flow follows: missing mandatory fields are asked for, placements are confirmed, a duplicate check runs, and the draft is reviewed before creation.',
    ],
  },
  'upload.sections': {
    title: 'Sections to extract',
    body: 'Each row names a template section to pull from the document. The template already tells Atlas the shape — single text or list, child rows (e.g. tasks under responsibilities).',
    bullets: [
      'Guidance is optional: the section name is usually enough. Write it only when the mapping is ambiguous — e.g. “build one requirement per skills-table row, keeping the stated proficiency wording”.',
      'Shape / Min / Max override the template’s defaults when a bound matters.',
      'A section the document doesn’t cover simply comes back empty.',
    ],
  },
  'upload.context': {
    title: 'Extraction context',
    body: 'Document conventions that apply to the whole extraction — terminology, structure quirks, language. E.g. “These are UK public-sector JDs; ‘person specification’ holds the requirements.”',
  },

  // ── Document create (form) ────────────────────────────────────────
  'form.field_source': {
    title: 'Field source',
    body: 'Fields from a template binds the form to a real template — captured values land on the created document, and the unified list below configures inputs and drafted sections. Custom fields is a free-standing form whose answers only feed the conversation.',
  },
  'form.on_submit': {
    title: 'On submit',
    bullets: [
      'Continue only: capture the values and follow the submitted link.',
      'Run a content chain: the selected chain drafts the content, then the document is created.',
      'Create directly: the document is created immediately with just the captured fields.',
      'Draft with Atlas: Atlas drafts the sections marked Output below in one pass — calibrated against the levelling framework — then creates the document. No chain involved.',
      'The “user choice” variants ask the user first: draft the content, or create with just these details?',
    ],
  },
  'form.unified_list': {
    title: 'Fields & sections',
    body: 'Everything the template offers, in the template’s own display order. Each row is one decision:',
    bullets: [
      'Ask on the form (input): the user fills it in; the value lands on the document.',
      'Draft with Atlas (output): Atlas writes this section on submit (drafting modes only).',
      'Ignored: not asked, not drafted.',
      '“Derived from input”: this value follows automatically from a hierarchy the form captures — nothing to configure.',
      '“Written by the chain”: in chain mode, the selected chain’s formatter owns this part.',
      'Tag parts (skills) use the Skills select instead: extracted skills, or skills with proficiency ratings.',
      'Not listed at all: labels, locked fields, decoration rows — they can’t be captured or drafted.',
      'An input beats a drafted value for the same part, so a row with a captured field can’t also be an output.',
    ],
  },
  'form.field_order': {
    title: 'Form field order',
    body: 'The exact top-to-bottom order the form presents its inputs — placement pickers and template fields together in one list. Use the arrows to reorder.',
    bullets: [
      'Newly enabled inputs slot in at their template position; reordering afterwards sticks.',
      'Only inputs appear here — drafted and ignored rows have no form position.',
    ],
  },
  'form.output_config': {
    title: 'Drafted section settings',
    body: 'Instructions are the editorial spec for the section — what good content looks like, phrasing conventions, level-calibration emphasis. Shape and the min/max bounds control structure; child bounds (e.g. “2 task rows per responsibility”) are enforced, not suggested.',
  },
  'form.context': {
    title: 'Drafting context',
    body: 'Persona and ground rules that apply to every drafted section — tone, house style, what never to mention, why accuracy matters. One or two sentences each. Section-specific conventions belong in that section’s instructions instead.',
  },

  // ── Validation / flow ─────────────────────────────────────────────
  'validation.publish': {
    title: 'Validation',
    body: 'Checks the definition the way the engine will resolve it at run time — template, attribute and section references must resolve for THIS client.',
    bullets: [
      'Errors block Publish; drafts still save so nothing is lost.',
      'The most common failure is a renamed template attribute or section: re-pick it from the list.',
    ],
  },
  'flow.editing': {
    title: 'Flow view',
    body: 'The same steps as the Steps tab, drawn as a graph. “Add step” here creates an unlinked step; each box’s ⋯ menu can add a linked step (wired to the next free outcome) or delete one. Full configuration still happens on the Steps tab — click a box to jump there.',
  },
};

/** Long-form guides — rendered by HelpGuideDialog.vue. */
export const HELP_GUIDES = {
  drafting: {
    title: 'Writing drafting instructions and context',
    intro:
      'The rule: instructions carry the editorial spec for a section — nothing the system already supplies. Most of what old chain prompts said is now automatic.',
    sections: [
      {
        heading: 'Supplied automatically — never write it into instructions',
        bullets: [
          'The captured inputs: every form value arrives labelled (title, family path with descriptors, level and track).',
          'Structure and counts: item shape, child rows, “N items”, “N tasks per responsibility” — the Shape and Min/Max fields enforce these; numbered-list artefacts can’t occur.',
          'The section’s own purpose and authoring guidance from template configuration, plus its word-check rules (bias words, counts).',
          'The levelling framework: every level’s descriptor and progression is injected with a calibration directive — “align with the level” works without pasting the framework in.',
          'Cross-section sight: all sections are drafted in one pass, so later sections see earlier ones.',
        ],
      },
      {
        heading: 'Write into the context box (once, applies to every section)',
        body:
          'Persona and ground rules. Example: “Use precise, skill-oriented language. Never mention brand-specific tools unless given. Never fabricate education levels or responsibilities beyond the role as described. This content feeds job evaluation, so level accuracy is critical.”',
      },
      {
        heading: 'Write into each section’s instructions',
        body: 'Definitions, level-calibration emphasis and phrasing conventions specific to that section. Working examples:',
        bullets: [
          'Responsibility — “A responsibility is a high-level summary of what the role holder is accountable for; its tasks are the key activities that deliver it. Align tone and complexity strictly with the provided level descriptor and stay safely below the next level. Responsibilities must not end with a full stop; tasks must.”',
          'Knowledge — “What someone needs to KNOW to perform at a fully acceptable level — knowledge only, never skills (those are captured elsewhere). Phrase each as ‘Have knowledge of…’, ‘Be familiar with…’, calibrated to the level.”',
          'Purpose — “A holistic summary derived from the responsibilities, aligned to the level. First sentence starts ‘This job…’, second ‘It…’, final sentence ‘Overall, the job…’.”',
        ],
      },
      {
        heading: 'The heuristic',
        body:
          '“Is this about what good content for this section looks like?” → instructions. “Is it about the document’s shape or the inputs?” → leave it out — the template and the form already say it.',
      },
    ],
  },
  extraction: {
    title: 'Guiding document extraction',
    intro:
      'Extraction is not authoring: Atlas preserves the document’s own content and wording, splitting it into the template’s sections. It never invents, and it never summarises. Most section rows need no guidance at all.',
    sections: [
      {
        heading: 'Automatic — nothing to configure',
        bullets: [
          'Attributes: every capturable field on the template is offered to the extraction — a value the document states is captured, anything absent is left for the normal gap-fill questions.',
          'Placements: each hierarchy (family, business unit…) is looked for by name and pre-fills the placement confirmation — the user always confirms.',
          'Shape: single text vs list, and child rows, come from the template.',
        ],
      },
      {
        heading: 'When to write section guidance',
        body: 'Only when the mapping from document to section is genuinely ambiguous:',
        bullets: [
          'The document has no such section — say where the content lives: “Build one requirement per row of the skills table, keeping the stated proficiency wording.”',
          'Two document sections could feed one template section: “Combine qualifications and experience bullets into Knowledge rows.”',
          'Structure needs interpreting: “Treat numbered duties as responsibilities and their sub-bullets as tasks.”',
        ],
      },
      {
        heading: 'The context box',
        body:
          'Document conventions that apply to the whole upload flow, not one section: “These are UK public-sector JDs; the ‘person specification’ section holds the requirements.”',
      },
      {
        heading: 'What NOT to write',
        bullets: [
          'Style, tone or level-calibration rules — extraction keeps the document’s wording, so authoring guidance has nothing to act on.',
          'Counts the document should dictate: leave Min/Max blank and take what the document has.',
        ],
      },
    ],
  },
};

export default WORKFLOW_HELP;
